<template>
  <div
    class="date-row -mx-[var(--sera-main-padding)] flex items-center gap-1 border-b border-solid border-gray-200 bg-white px-4 py-2 [&:not(:first-child)]:-mt-4"
    data-cy="schedule-date-row"
  >
    <button
      type="button"
      class="flex h-10 w-7 shrink-0 items-center justify-center rounded-lg text-gray-500"
      :aria-label="`Previous ${stepUnit}`"
      data-cy="schedule-date-prev"
      @click="step(-1)"
    >
      <i class="fa-regular fa-chevron-left" />
    </button>

    <div class="relative min-w-0 flex-1">
      <button
        type="button"
        class="flex w-full items-center justify-center gap-0.5 text-sm font-semibold text-gray-900"
        data-cy="schedule-date-title"
        @click="toggleCalendar"
      >
        <!--
          w-max keeps the label the width of its text so the caret stays beside
          the date: as a plain flex child its auto basis resolves to the whole
          button, which on an 800px tablet left the caret stranded 480px away.
          max-w-full hands the width back when the text is longer than the room,
          and truncate then does its job.
        -->
        <span class="w-max max-w-full truncate">{{ title }}</span>
        <!-- shrink-0: squashing the caret would give the label width it has not
             really got, and the truncation would then hide real characters. -->
        <i class="fa-regular fa-chevron-down shrink-0 text-xs text-gray-500" />
      </button>

      <div
        v-if="calendarOpen"
        class="absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg"
        data-cy="schedule-date-calendar"
      >
        <ClickOutside
          :exclude-classes="['datepicker-cell']"
          @click-outside="closeCalendar"
        >
          <div class="py-2">
            <Calendar v-model="calendarDate" :min-date="null" />
            <div class="mt-2 flex gap-3 px-2">
              <Button
                type="Secondary"
                label="Today"
                class="w-1/3"
                data-cy="schedule-date-calendar-today"
                @click="selectToday"
              />
              <Button
                label="Update"
                class="w-2/3"
                data-cy="schedule-date-calendar-update"
                @click="applyCalendarDate"
              />
            </div>
          </div>
        </ClickOutside>
      </div>
    </div>

    <button
      type="button"
      class="flex h-10 w-7 shrink-0 items-center justify-center rounded-lg text-gray-500"
      :aria-label="`Next ${stepUnit}`"
      data-cy="schedule-date-next"
      @click="step(1)"
    >
      <i class="fa-regular fa-chevron-right" />
    </button>

    <button
      type="button"
      class="flex h-10 w-9 shrink-0 items-center justify-center rounded-lg border border-solid border-gray-200 text-gray-500 disabled:opacity-40"
      :disabled="showingToday"
      aria-label="Return to today"
      data-cy="schedule-return-to-today"
      @click="returnToToday"
    >
      <i class="fa-regular fa-arrow-turn-down-left" />
    </button>

    <ScheduleViewToggle v-if="showViewToggle" class="shrink-0" />
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import Calendar from "@/components/Calendar.vue";
import ClickOutside from "@/components/ClickOutside.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import { scheduleBoardDateLabel } from "@/Scheduler/scheduleBoardDateLabel";
import ScheduleViewToggle from "@/Scheduler/ScheduleViewToggle.vue";

/**
 * The phone scheduler's date row: which day or week the board is showing, the
 * four ways to move it, and the Day / Week switch.
 *
 * Week state is not held here. `ScheduleViewToggle` already owns it in
 * `scheduleBoardFilters.view`, so this row reads the same getter the desktop
 * board reads and both surfaces agree about which week is showing. That is
 * also why the arrows ask the store rather than a local ref for their step.
 *
 * The toggle is HIDDEN, not disabled, once two or more technicians are
 * selected: a week of several technicians has no single timeline to draw, and
 * a disabled control would still hold width this row has none to spare.
 *
 * The page stacks its rows in a `gap-4` flex column, which puts 16px between
 * the control row's bottom border and this row's own top padding while nothing
 * balances it below -- the bottom border is this row's own edge. That reads as
 * a row whose content sits low in its band (measured: 24px above the ink, 8px
 * below). The negative top margin cancels that gap so the two rows meet as one
 * chrome stack and the content centres between the two rules. Guarded on
 * `:not(:first-child)` because this row renders on `showControlRow` alone while
 * the control row above it also needs `activeResource` -- without the guard,
 * the row with no sibling would pull itself up into the banner.
 *
 * Only the horizontal padding is cancelled, so the row spans the same width as
 * the technician control row above it. It is deliberately NOT sticky: the row
 * above it is, and pins only when a schedule is selected, so the offset this
 * one would need differs between those two states. Picking one needs the
 * rendered heights, which is a browser measurement — see the E2E spec.
 */
const props = defineProps<{ date: Date; technicianCount: number }>();

const emit = defineEmits<{
  (e: "updateSelectedDate", date: Date): void;
  (e: "returnToToday"): void;
  (e: "stepped", unit: "day" | "week"): void;
}>();

const store = useStore();
const isWeekView = computed<boolean>(
  () => store.getters.scheduleWeekViewActive
);

const stepUnit = computed<"day" | "week">(() =>
  isWeekView.value ? "week" : "day"
);
const showViewToggle = computed(() => props.technicianCount < 2);

// Noon, matching ScheduleHeader: a date pinned to midnight lands on the
// previous day once a tenant timezone west of the browser's is applied, and the
// row would then name a different day than the header above it.
const title = computed(() =>
  scheduleBoardDateLabel(
    moment(props.date).hours(12).toDate(),
    isWeekView.value,
    { compact: true }
  )
);

// Reads the visible date, never the last navigation: arriving at today through
// the calendar or the arrows has to disable this exactly as the button does.
// In week view the whole visible week counts as today's.
const showingToday = computed(() =>
  moment(props.date).isSame(new Date(), isWeekView.value ? "isoWeek" : "day")
);

const step = (delta: number) => {
  emit(
    "updateSelectedDate",
    moment(props.date).add(delta, stepUnit.value).toDate()
  );
  emit("stepped", stepUnit.value);
};

// Two events for one tap: the date change the board acts on, and the intent,
// which is what gets measured. Inferring the second from the first would count
// any arrow that happens to land on today.
const returnToToday = () => {
  emit("updateSelectedDate", new Date());
  emit("returnToToday");
};

const calendarOpen = ref(false);
const calendarDate = ref<Date>(props.date);

const toggleCalendar = () => (calendarOpen.value = !calendarOpen.value);
const closeCalendar = () => (calendarOpen.value = false);
const selectToday = () => (calendarDate.value = new Date());

const applyCalendarDate = () => {
  if (!calendarDate.value) return;
  emit("updateSelectedDate", calendarDate.value);
  calendarOpen.value = false;
};

watch(
  () => props.date,
  (newVal, oldVal) => {
    if (!moment(newVal).isSame(oldVal, "day")) calendarDate.value = newVal;
  }
);
</script>

<style scoped>
/*
 * ScheduleViewToggle is sized for the desktop board, where width is free. Here
 * it shares 360px with four controls and the date, and its default segment
 * padding left the title 54px for a label needing 87 — "Fri 11 Sep" rendered as
 * "Fri…" on a 360px phone. Narrowing the segments here rather than in the shared
 * component keeps the desktop board untouched; the segments keep their full
 * height, so the tap target does not shrink.
 */
/*
 * !important is required, not preferred: tailwind.config.js sets
 * `important: true`, so the toggle's own `px-4` utility carries it and no
 * amount of selector specificity beats that.
 */
.date-row :deep(.view-toggle-btn) {
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}
</style>
